import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Check, Mic, Video, Square, X, Clock } from 'lucide-react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Label } from './ui/label';

const PODCAST_PACKAGES = [
  {
    id: null,
    name: 'Sin Podcast',
    price: 0,
    description: 'No incluir servicio de podcast',
    features: []
  },
  {
    id: 'pod_essential',
    name: 'Essential',
    price: 997,
    description: '1hr de Grabación en tiempo real. No incluye Post-Producción. Se entregan los materiales en crudo tras finiquito.',
    features: [
      '1 hora de grabación en tiempo real',
      'Hasta 2 micrófonos',
      '12 Shorts de 10s',
      'Iluminación y Sets Locales',
      'Materiales entregados en crudo vía nube'
    ]
  },
  {
    id: 'pod_platino',
    name: 'Platino',
    price: 3500,
    description: '1hr de Grabación de Video a 1 Cámara. No incluye Post-Producción. Se entregan los materiales en crudo tras finiquito.',
    features: [
      '1 hora de grabación en tiempo real',
      'Video a 1 Cámara',
      '12 Shorts de 10s',
      'Iluminación y Sets Locales',
      'Materiales entregados en crudo vía nube'
    ]
  },
  {
    id: 'pod_premium',
    name: 'Premium',
    price: 5500,
    description: '1hr de Grabación de Video a 2 Cámaras. No incluye Post-Producción. Se entregan los materiales en crudo tras finiquito.',
    features: [
      '1 hora de grabación en tiempo real',
      'Video a 2 Cámaras',
      '12 Shorts de 10s',
      'Iluminación y Sets Locales',
      'Materiales entregados en crudo vía nube'
    ]
  }
];

const PODCAST_ADDONS = [
  {
    id: 'pod_set_basico',
    name: 'Construcción de Set Básico',
    price: 5500,
    perProgram: false
  },
  {
    id: 'pod_set_virtual',
    name: 'Diseño de Set Virtual',
    price: 2500,
    perProgram: false
  }
];

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 bg-[#aa2aff] text-white text-xs rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-[#080c2d]/50">
          {children}
        </div>
      )}
    </div>
  );
}

function PodcastPackageCard({ pkg, isSelected, onSelect, formatPrice, currency, showPerHour = false }) {
  return (
    <button
      onClick={() => onSelect(pkg.id)}
      data-testid={`btn-podcast-${pkg.id}`}
      className={cn(
        "flex flex-col p-4 rounded-xl border text-left transition-all",
        "bg-gradient-to-b from-[#1a1f50]/80 to-[#080c2d]/90",
        isSelected 
          ? "border-[#aa2aff] shadow-[0_0_20px_rgba(170,42,255,0.3)]" 
          : "border-white/10 hover:border-[#aa2aff]/50"
      )}
    >
      <div className="flex justify-between items-start mb-2">
        <h4 className={cn(
          "font-bold",
          isSelected ? "text-[#aa2aff]" : "text-white"
        )}>
          {pkg.name}
        </h4>
        <span className="text-lg font-bold text-white">
          {formatPrice(pkg.price)} 
          <span className="text-xs text-slate-400">
            {showPerHour && pkg.price > 0 ? '/hr' : ` ${currency}`}
          </span>
        </span>
      </div>
      <p className="text-xs text-slate-400 mb-3">{pkg.description}</p>
      <div className="mt-auto">
        <div className={cn(
          "py-1.5 text-center text-xs font-semibold rounded-lg transition-all",
          isSelected 
            ? "bg-[#aa2aff] text-white" 
            : "bg-white/5 text-slate-400"
        )}>
          {isSelected ? 'Seleccionado' : 'Seleccionar'}
        </div>
      </div>
    </button>
  );
}

export function PodcastSection({ 
  isOpen, 
  onToggle, 
  selectedPackage, 
  onPackageSelect,
  selectedAddons,
  onAddonToggle,
  podcastQuantity = 1,
  onQuantityChange,
  podcastData = {},
  onPodcastDataChange,
  postProduction = false,
  onPostProductionChange,
  formatPrice, 
  currency,
  servicePrices = {}
}) {
  // Build dynamic packages with prices from settings
  const podcastPackages = [
    {
      id: null,
      name: 'Sin Podcast',
      price: 0,
      description: 'No incluir servicio de podcast',
      features: []
    },
    {
      id: 'pod_essential',
      name: 'Essential',
      price: servicePrices.price_pod_essential || 997,
      description: 'No incluye Post-Producción. Se entregan los materiales en crudo vía nube tras finiquito.',
      features: [
        '1 hora de grabación en tiempo real',
        'Hasta 2 micrófonos',
        '12 Shorts de 10s',
        'Iluminación y Sets Locales',
        'Materiales entregados en crudo vía nube'
      ]
    },
    {
      id: 'pod_platino',
      name: 'Platino',
      price: servicePrices.price_pod_platino || 3500,
      description: 'Video a 1 Cámara. No incluye Post-Producción. Se entregan los materiales en crudo vía nube tras finiquito.',
      features: [
        '1 hora de grabación en tiempo real',
        'Video a 1 Cámara',
        '12 Shorts de 10s',
        'Iluminación y Sets Locales',
        'Materiales entregados en crudo vía nube'
      ]
    },
    {
      id: 'pod_premium',
      name: 'Premium',
      price: servicePrices.price_pod_premium || 5500,
      description: 'Video a 2 Cámaras. No incluye Post-Producción. Se entregan los materiales en crudo vía nube tras finiquito.',
      features: [
        '1 hora de grabación en tiempo real',
        'Video a 2 Cámaras',
        '12 Shorts de 10s',
        'Iluminación y Sets Locales',
        'Materiales entregados en crudo vía nube'
      ]
    }
  ];

  const podcastAddons = [
    {
      id: 'pod_set_basico',
      name: 'Construcción de Set Básico',
      price: servicePrices.price_pod_set_basico || 5500,
      perProgram: false  // Cobrar solo una vez, no por programa
    },
    {
      id: 'pod_set_virtual',
      name: 'Diseño de Set Virtual',
      price: servicePrices.price_pod_set_virtual || 2500,
      perProgram: false  // Cobrar solo una vez, no por programa
    }
  ];

  const selectedPkg = podcastPackages.find(p => p.id === selectedPackage);
  
  // Calculate addons - some are per program, some are one-time
  const addonsOneTime = selectedAddons.reduce((sum, addonId) => {
    const addon = podcastAddons.find(a => a.id === addonId);
    // Only add if it's NOT per program (one-time charge)
    return sum + (addon && !addon.perProgram ? addon.price : 0);
  }, 0);
  
  const addonsPerProgram = selectedAddons.reduce((sum, addonId) => {
    const addon = podcastAddons.find(a => a.id === addonId);
    // Only add if it IS per program
    return sum + (addon && addon.perProgram ? addon.price : 0);
  }, 0);

  // Calculate estimated recording hours: (quantity × duration_minutes × 2.5) / 60
  const estimatedRecordingHours = podcastData.episodeDuration 
    ? (podcastQuantity * podcastData.episodeDuration * 2.5) / 60 
    : 0;

  // Total calculation:
  // - Package price × recording hours (or × quantity if no duration)
  // - + addons per program × quantity
  // - + one-time addons (not multiplied)
  // - If post-production, double the package portion
  const postProdMultiplier = postProduction ? 2 : 1;
  const packageTotal = selectedPkg?.price 
    ? (estimatedRecordingHours > 0 
        ? ((selectedPkg.price * estimatedRecordingHours) * postProdMultiplier) + (addonsPerProgram * podcastQuantity) + addonsOneTime
        : ((selectedPkg.price * podcastQuantity) * postProdMultiplier) + (addonsPerProgram * podcastQuantity) + addonsOneTime)
    : 0;
    
  const badge = selectedPackage ? `${formatPrice(packageTotal)}` : null;

  const handleQuantityChange = (value) => {
    const numValue = parseInt(value, 10);
    if (numValue >= 1 && numValue <= 500) {
      onQuantityChange(numValue);
    }
  };

  const handleDataChange = (field, value) => {
    onPodcastDataChange({ ...podcastData, [field]: value });
  };

  return (
    <CollapsibleSection
      title="Podcasts"
      icon={Mic}
      isOpen={isOpen}
      onToggle={onToggle}
      badge={badge}
    >
      <div className="space-y-4">
        {/* Podcast Data Section */}
        <div className="bg-[#1a1f50]/50 rounded-xl p-4 border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Mic className="w-4 h-4 text-[#aa2aff]" />
            Datos del Podcast
          </h3>
          
          <div className="space-y-4">
            {/* Podcast Title */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Título del Podcast o Curso</Label>
              <Input
                placeholder="Ej: Mi Podcast de Negocios"
                value={podcastData.title || ''}
                onChange={(e) => handleDataChange('title', e.target.value)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-10"
                data-testid="input-podcast-title"
              />
            </div>

            {/* Quantity and Duration Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Cant. de Programas / Episodios</Label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={podcastQuantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-10"
                  data-testid="podcast-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Duración por Episodio (min)</Label>
                <Input
                  type="number"
                  min="1"
                  max="180"
                  placeholder="Ej: 30"
                  value={podcastData.episodeDuration || ''}
                  onChange={(e) => handleDataChange('episodeDuration', parseInt(e.target.value) || 0)}
                  className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-10"
                  data-testid="input-episode-duration"
                />
              </div>
            </div>

            {/* Estimated Recording Hours - Auto calculated */}
            {estimatedRecordingHours > 0 && (
              <div className="bg-[#aa2aff]/10 rounded-lg p-3 border border-[#aa2aff]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#aa2aff]" />
                    <span className="text-slate-300 text-sm">Horas de Grabación Estimadas</span>
                  </div>
                  <span className="text-white font-bold text-lg">
                    {estimatedRecordingHours.toFixed(1)} hrs
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Fórmula: ({podcastQuantity} episodios × {podcastData.episodeDuration} min × 2.5) ÷ 60
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Package Selection */}
        <div>
          <h3 className="text-white font-semibold mb-3">Selecciona tu Paquete de Podcast</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {podcastPackages.map(pkg => (
              <PodcastPackageCard
                key={pkg.id}
                pkg={pkg}
                isSelected={selectedPackage === pkg.id}
                onSelect={onPackageSelect}
                formatPrice={formatPrice}
                currency={currency}
                showPerHour={estimatedRecordingHours > 0}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            {estimatedRecordingHours > 0 
              ? `Precio por hora de grabación × ${estimatedRecordingHours.toFixed(1)} horas estimadas`
              : 'Todos los paquetes incluyen: 12 Shorts de 10s • Iluminación y Sets Locales'}
          </p>
        </div>

        {/* Post-Production Toggle */}
        {selectedPackage && (
          <div className="pt-4 border-t border-white/10">
            <button
              onClick={() => onPostProductionChange && onPostProductionChange(!postProduction)}
              className={cn(
                "w-full p-4 rounded-xl border text-left transition-all",
                postProduction
                  ? "border-[#aa2aff] bg-gradient-to-r from-[#aa2aff]/20 to-[#7012b2]/10 shadow-[0_0_20px_rgba(170,42,255,0.15)]"
                  : "border-white/10 bg-white/5 hover:border-[#aa2aff]/40"
              )}
              data-testid="btn-post-production"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className={cn("font-bold text-sm", postProduction ? "text-[#aa2aff]" : "text-white")}>
                    Paquete de Post-Producción
                  </h4>
                  <p className="text-slate-400 text-xs mt-1">
                    {postProduction
                      ? 'Incluye Post-Producción. Los materiales terminados serán entregados por medio de nuestra nube tras finiquito.'
                      : 'Agregar post-producción de audio y video. Duplica el valor del paquete seleccionado.'}
                  </p>
                </div>
                <div className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-semibold transition-all",
                  postProduction ? "bg-[#aa2aff] text-white" : "bg-white/10 text-slate-400"
                )}>
                  {postProduction ? 'Incluido' : 'Agregar'}
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Addons */}
        {selectedPackage && (
          <div className="pt-4 border-t border-white/10">
            <h3 className="text-white font-semibold mb-3">Servicios Adicionales</h3>
            <div className="space-y-2">
              {podcastAddons.map(addon => (
                <label
                  key={addon.id}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all",
                    selectedAddons.includes(addon.id)
                      ? "border-[#aa2aff] bg-[#aa2aff]/10"
                      : "border-white/10 hover:border-white/20 bg-white/5"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded border-2 flex items-center justify-center",
                      selectedAddons.includes(addon.id)
                        ? "border-[#aa2aff] bg-[#aa2aff]"
                        : "border-slate-500"
                    )}>
                      {selectedAddons.includes(addon.id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-white text-sm">{addon.name}</span>
                  </div>
                  <span className="text-white font-bold text-sm">
                    +{formatPrice(addon.price)} {currency}
                    {addon.perProgram && podcastQuantity > 1 && <span className="text-slate-400 font-normal"> x {podcastQuantity}</span>}
                  </span>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={selectedAddons.includes(addon.id)}
                    onChange={() => onAddonToggle(addon.id)}
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Total Summary */}
        {selectedPackage && packageTotal > 0 && (
          <div className="pt-4 border-t border-white/10">
            <div className="flex justify-between items-center text-lg">
              <span className="text-white font-semibold">Subtotal Podcast:</span>
              <span className="text-[#aa2aff] font-bold">{formatPrice(packageTotal)} {currency}</span>
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

// Export constants
export { PODCAST_PACKAGES, PODCAST_ADDONS };

export default PodcastSection;
